import { NavContext } from '@ionic/react';
import { type RefObject, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { triggerTapHaptic } from '@/core/lib/dnd-haptics';
import {
  type AppTab,
  getMoreTabStackRoot,
  isMoreTabSubPath,
  resolveTabForPath,
  TAB_MORE,
} from '@/core/router/app-routes';

/**
 * Resets the More tab stack when leaving a pushed More page via the bottom tab bar.
 *
 * Ionic tracks per-tab `currentHref` separately from the visible route. Leaving More
 * without calling `resetTab` keeps sub-pages (e.g. product detail) in the tab stack
 * and in the DOM. The reset runs on `ionTabsDidChange` (after the new tab is shown)
 * so users do not briefly see the More hub during cross-tab switches.
 *
 * @see IonTabBarUnwrapped.onTabButtonClick / handleResetTab in @ionic/react
 */
export function useMoreTabStackReset(
  tabBarHostRef: RefObject<HTMLElement | null>,
) {
  const location = useLocation();
  const nav = useContext(NavContext);

  const navRef = useRef(nav);
  const locationPathRef = useRef(location.pathname);
  const currentTabRef = useRef<AppTab | undefined>(
    resolveTabForPath(location.pathname),
  );
  const pendingMoreStackResetRef = useRef(false);

  useEffect(() => {
    navRef.current = nav;
  }, [nav]);

  useEffect(() => {
    locationPathRef.current = location.pathname;
    currentTabRef.current = resolveTabForPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const host = tabBarHostRef.current;
    if (!host) {
      return;
    }

    const tabsHost = host.closest('ion-tabs');
    if (!tabsHost) {
      return;
    }

    const onTabsDidChange = () => {
      if (!pendingMoreStackResetRef.current) {
        return;
      }

      pendingMoreStackResetRef.current = false;

      const navigation = navRef.current;
      if (!navigation?.resetTab) {
        return;
      }

      navigation.resetTab(TAB_MORE, getMoreTabStackRoot(), undefined);
    };

    tabsHost.addEventListener('ionTabsDidChange', onTabsDidChange);

    return () => {
      tabsHost.removeEventListener('ionTabsDidChange', onTabsDidChange);
    };
  }, [tabBarHostRef]);

  useEffect(() => {
    const host = tabBarHostRef.current;
    if (!host) {
      return;
    }

    const onTabBarClickCapture = (event: Event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const tabButton = event.target.closest('ion-tab-button');
      if (!tabButton || !host.contains(tabButton)) {
        return;
      }

      const tab = tabButton.getAttribute('tab') as AppTab | null;
      if (!tab) {
        return;
      }

      const navigation = navRef.current;
      if (!navigation?.changeTab || !navigation?.resetTab) {
        return;
      }

      const fromTab = currentTabRef.current;
      const fromPath = locationPathRef.current;
      const moreRoot = getMoreTabStackRoot();

      const leavingMoreDeep =
        fromTab === TAB_MORE && tab !== TAB_MORE && isMoreTabSubPath(fromPath);
      const enteringMore = tab === TAB_MORE && fromTab !== TAB_MORE;

      if (enteringMore) {
        event.preventDefault();
        event.stopImmediatePropagation();
        void triggerTapHaptic();
        navigation.changeTab(TAB_MORE, moreRoot, undefined);
        return;
      }

      if (leavingMoreDeep) {
        // Defer until after Ionic switches tabs so the hub reset is not visible.
        pendingMoreStackResetRef.current = true;
      }
    };

    host.addEventListener('click', onTabBarClickCapture, true);

    return () => {
      host.removeEventListener('click', onTabBarClickCapture, true);
    };
  }, [tabBarHostRef]);
}
